'use client'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'

import type {
  BuildingBlockPostType,
  CartItemProps,
  RoundtripPostType,
} from '@typings'
import { CardLazyWrapper } from '@components/cards/RaCardLazyWrapper/RaCardLazyWrapper'
import { RaPaper, RaTypography } from '@components/core/index.core'
import { useCart } from '@contexts/cart.context'
import { Loop, translate } from '@utilities/helpers/core.helpers'

import { API } from '@/api/index.api'
import { useSettings } from '@/contexts/settings.context'
import { base64Decode } from '@/utilities/helpers/base64decode.helpers'
import { makeCartItem } from '@/utilities/helpers/make-cart-item.helpers'

type FulFilledBlock = RoundtripPostType | BuildingBlockPostType

export const RaTravelPlanCards = () => {
  const cart = useCart()
  const settings = useSettings()

  const params = useSearchParams()
  const travelitems = params.get('travelitems')

  const fetchCartItems = async items => {
    const promises = []
    items.forEach(item => {
      if (item.postType === 'rundreise') {
        promises.push(API.Roundtrips.show(item.postId || item.parent))
      } else {
        promises.push(API.BuildingBlocks.show(item.postId || item.parent))
      }
    })
    const data = await Promise.allSettled(promises)
    const completed = data.filter(item => item.status === 'fulfilled')
    const fulfilled: FulFilledBlock[] = completed
      .filter(item => !item.value?.data?.error)
      .map(item => item.value?.data)

    if (fulfilled.length) {
      if (cart.cartItems?.length) {
        cart.clearCart()
      }
      const extractVariants = items.map(item => {
        if (item.postId) {
          const product = fulfilled.find(({ id }) => id === item.postId)
          return makeCartItem(settings.destination, product)
        } else {
          const parent = fulfilled.find(
            res => res.id.toString() === item.parent
          )
          if (parent.type === 'reise-baustein') {
            const child = parent.acf.bb_additional_services_repeater.find(
              ({ gid }) => gid === item.gid
            )
            return makeCartItem(settings.destination, child, item.parent)
          }
        }
      })
      // Second argument to skip modal
      cart.addToCart(extractVariants, true)
    }
  }

  useEffect(() => {
    if (travelitems) {
      const decoded = base64Decode(travelitems)
      fetchCartItems(decoded)
    }
  }, [travelitems])

  const cartItems = useMemo(() => {
    if (!cart?.cartItems || cart.cartItemsCount === 0)
      return (
        <RaTypography
          weight="semibold"
          size="l"
          align="center">
          {translate('BASKET_IS_EMPTY')}
        </RaTypography>
      )

    return (
      <ul>
        {Loop(cart.cartItems, (item: CartItemProps, i: number) => (
          <CardLazyWrapper
            renderCard="travelplan"
            key={i}
            {...{ props: { item, index: i } }}
          />
        ))}
      </ul>
    )
  }, [cart?.cartItems])

  return (
    <div style={{ marginBottom: 24 }}>
      <RaPaper
        paddingMobile={4}
        padding={5}>
        {cartItems}
      </RaPaper>
    </div>
  )
}
